import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap, type Schemas } from './client';

export type Webhook = Schemas['WebhookDto'];
export type WebhookRequest = Schemas['WebhookRequest'];
export type WebhookEvent = Webhook['events'][number];
export type WebhookDelivery = Schemas['Delivery'];

const key = (ws: string) => ['workspace', ws, 'webhooks'] as const;

export function useWebhooks(ws: string, enabled = true) {
  return useQuery({
    queryKey: key(ws),
    queryFn: () => unwrap(api.GET('/api/v1/workspaces/{workspaceId}/webhooks', { params: { path: { workspaceId: ws } } })),
    enabled,
  });
}

export function useWebhookEvents(ws: string, enabled = true) {
  return useQuery({
    queryKey: [...key(ws), 'events'],
    queryFn: () => unwrap(api.GET('/api/v1/workspaces/{workspaceId}/webhooks/events', { params: { path: { workspaceId: ws } } })),
    enabled,
    staleTime: Infinity,
  });
}

export function useSaveWebhook(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: WebhookRequest & { id?: string }) =>
      id
        ? unwrap(api.PATCH('/api/v1/workspaces/{workspaceId}/webhooks/{id}', { params: { path: { workspaceId: ws, id } }, body }))
        : unwrap(api.POST('/api/v1/workspaces/{workspaceId}/webhooks', { params: { path: { workspaceId: ws } }, body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(ws) }),
  });
}

export function useDeleteWebhook(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(api.DELETE('/api/v1/workspaces/{workspaceId}/webhooks/{id}', { params: { path: { workspaceId: ws, id } } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(ws) }),
  });
}

export function useWebhookAction(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action, deliveryId }: { id: string; action: 'ping' | 'rotate' | 'redeliver'; deliveryId?: string }) => {
      const path = { workspaceId: ws, id };
      if (action === 'ping') return unwrap(api.POST('/api/v1/workspaces/{workspaceId}/webhooks/{id}/ping', { params: { path } }));
      if (action === 'rotate')
        return unwrap(api.POST('/api/v1/workspaces/{workspaceId}/webhooks/{id}/rotate-secret', { params: { path } }));
      return unwrap(
        api.POST('/api/v1/workspaces/{workspaceId}/webhooks/{id}/deliveries/{deliveryId}/redeliver', {
          params: { path: { ...path, deliveryId: deliveryId! } },
        }),
      );
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: key(ws) }),
  });
}

export function useWebhookDeliveries(ws: string, id: string | null) {
  return useQuery({
    queryKey: [...key(ws), id, 'deliveries'],
    queryFn: () =>
      unwrap(
        api.GET('/api/v1/workspaces/{workspaceId}/webhooks/{id}/deliveries', { params: { path: { workspaceId: ws, id: id! } } }),
      ),
    enabled: !!id,
    refetchInterval: 5000,
  });
}
